import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Target, CalendarCheck, BookOpen, GraduationCap, Paperclip, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, tone = "text-primary" }) => (
  <Card className="p-4 border-slate-200" data-testid="sup-stat">
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div><div className="text-2xl font-bold text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </div>
  </Card>
);

export default function Supervision() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/pedagogy/supervision").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Erreur")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!d) return null;

  return (
    <div data-testid="pedagogy-supervision-page">
      <div className="flex items-center gap-2 mb-1"><Target className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">Suivi pédagogique</h1></div>
      <p className="text-sm text-slate-500 mb-6">Semaine {d.week_label}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={CalendarCheck} label="Séances renseignées" value={`${d.sessions_filled}/${d.sessions_planned}`} tone="text-emerald-600" />
        <Stat icon={AlertTriangle} label="Séances à compléter" value={d.sessions_to_fill} tone="text-amber-600" />
        <Stat icon={BookOpen} label="Devoirs (14 j)" value={d.homework_recent} />
        <Stat icon={GraduationCap} label="Évaluations (14 j)" value={d.evaluations_recent} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200">
          <h2 className="font-display font-semibold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Séances à compléter</h2>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="sup-to-fill">
            {(d.to_fill || []).length === 0 ? <p className="text-sm text-slate-400">Tout est à jour. 🎉</p>
              : d.to_fill.map((s, i) => (
                <div key={i} className="text-sm flex items-center justify-between border-b pb-1.5">
                  <span className="text-slate-700">{s.group_name || "—"} · {s.subject_name || "—"}</span>
                  <span className="text-xs text-slate-400">{s.date} {s.start_time} · {s.teacher_name || "—"}</span>
                </div>
              ))}
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <h2 className="font-display font-semibold text-slate-800 mb-3">Groupes sans activité récente</h2>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="sup-no-activity">
            {(d.groups_no_activity || []).length === 0 ? <p className="text-sm text-slate-400">Tous les groupes ont une activité.</p>
              : d.groups_no_activity.map((g, i) => <Badge key={i} variant="secondary" className="mr-1 mb-1">{g.group_name}</Badge>)}
          </div>
          <div className="mt-4 text-sm text-slate-500 flex items-center gap-2"><Paperclip className="h-4 w-4" />Pièces jointes déposées (14 j) : <strong className="text-slate-800">{d.attachments_recent}</strong></div>
        </Card>
      </div>
    </div>
  );
}
